// TicketsWidget.jsx
import React, { useState } from "react";
import { Brain, Maximize2, Ticket } from "lucide-react";

const card = "rounded-2xl border border-[#00ff90]/20 bg-[#080808] shadow-[0_0_24px_#00ff9010]";

export function TicketsWidget({ data = {}, period, loading, onAskKira, onExpand, expanded }) {
  const [tab, setTab] = useState("overview");
  const byPriority = data.by_priority    || [];
  const byDept     = data.by_department  || [];
  const trend      = data.resolution_trend || [];

  return (
    <div className={`${card} flex flex-col`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
        <div className="flex items-center gap-2">
          <Ticket size={14} className="text-[#fb923c]/60" />
          <span className="text-sm font-semibold text-white/80">Tickets & Operations</span>
          {(data.open_p1 > 0) && (
            <span className="text-xs bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-0.5 rounded-full ml-1">
              {data.open_p1} P1 open
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => onAskKira(data, "Analyse ticket health: open P1s, SLA breaches, resolution times by department. What operational risks exist and what actions should I take?")}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-[#fb923c]/20 bg-[#fb923c]/5 text-[#fb923c]/60 hover:text-[#fb923c] transition-colors">
            <Brain size={11} /> KIRA
          </button>
          {onExpand && <button onClick={onExpand} className="text-white/20 hover:text-white transition-colors"><Maximize2 size={13} /></button>}
        </div>
      </div>

      <div className="flex gap-1 px-4 pt-3">
        {["overview","priority","departments","trend"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`text-xs px-3 py-1.5 rounded-lg transition-colors capitalize ${
              tab === t ? "bg-[#fb923c]/10 text-[#fb923c] border border-[#fb923c]/20" : "text-white/30 hover:text-white/60"
            }`}>{t}</button>
        ))}
      </div>

      <div className="p-5 flex-1 space-y-4">
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-8 rounded-lg bg-white/5 animate-pulse" />)}</div>
        ) : (
          <>
          {tab === "overview" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "Total Open",     value: data.total_open ?? "—",   color: "#fb923c" },
                  { label: "P1 Open",        value: data.open_p1 ?? "—",      color: data.open_p1 > 0 ? "#f87171" : "#34d399" },
                  { label: "SLA Breach Rate",value: data.sla_breach_rate ? `${data.sla_breach_rate}%` : "—", color: data.sla_breach_rate > 10 ? "#f87171" : "#00ff90" },
                  { label: "Avg Resolution", value: data.avg_resolution ?? "—", color: "#60a5fa" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="rounded-xl border border-white/5 bg-white/3 p-3">
                    <p className="text-xs text-white/30 mb-1">{label}</p>
                    <p className="text-xl font-bold font-mono" style={{ color }}>{value}</p>
                  </div>
                ))}
              </div>
              {data.open_p1 > 0 && (
                <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3">
                  <p className="text-sm text-red-400 font-medium">{data.open_p1} critical P1 ticket{data.open_p1 !== 1 ? "s" : ""} open</p>
                  <p className="text-xs text-white/30 mt-0.5">These require immediate attention.</p>
                </div>
              )}
            </div>
          )}

          {tab === "priority" && (
            <div className="space-y-3">
              {byPriority.length === 0 ? <p className="text-white/20 text-sm italic text-center py-6">No data.</p>
              : byPriority.map((p, i) => {
                const PCOL = { P1: "#f87171", P2: "#fb923c", P3: "#fbbf24", P4: "#34d399" };
                const color = PCOL[p.priority] || "#60a5fa";
                const max = Math.max(...byPriority.map(x => x.open || 0), 1);
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs font-mono w-6" style={{ color }}>{p.priority}</span>
                    <div className="flex-1 h-2 rounded-full bg-white/5">
                      <div className="h-full rounded-full" style={{ width: `${Math.round((p.open/max)*100)}%`, background: `${color}60` }} />
                    </div>
                    <span className="text-xs font-mono text-white/50 w-8 text-right">{p.open} open</span>
                    {p.breached > 0 && <span className="text-xs text-red-400/60">({p.breached} breached)</span>}
                  </div>
                );
              })}
            </div>
          )}

          {tab === "departments" && (
            <div className="space-y-2">
              {byDept.length === 0 ? <p className="text-white/20 text-sm italic text-center py-6">No data.</p>
              : byDept.sort((a,b) => (b.open||0)-(a.open||0)).map((d, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/3 px-4 py-3">
                  <span className="text-sm text-white/60">{d.dept}</span>
                  <div className="flex gap-4 text-xs font-mono">
                    <span className="text-[#fb923c]/70">{d.open} open</span>
                    {d.breached > 0 && <span className="text-red-400/60">{d.breached} breached</span>}
                    <span className="text-white/30">{d.avg_resolution} avg</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "trend" && (
            <div className="space-y-3">
              {trend.length === 0 ? <p className="text-white/20 text-sm italic text-center py-6">No trend data.</p>
              : trend.map((t, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-[10px] text-white/25 w-16 shrink-0">{t.label}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-white/5">
                    <div className="h-full rounded-full bg-[#fb923c]/50"
                      style={{ width: `${Math.min((t.avg_hours / 48) * 100, 100)}%` }} />
                  </div>
                  <span className="text-xs font-mono text-white/40 w-12 text-right">{t.avg_hours}h avg</span>
                </div>
              ))}
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}

export default TicketsWidget;